import React, { useMemo, useState, useCallback } from "react";
import {
	Box,
	Button,
	CardContent,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TablePagination,
	Typography
} from "@mui/material";
import { RemoveCircleOutline, ExpandMore } from "@mui/icons-material";
import { StyledCard } from "./styled";
import { MobileCard } from "../styled";
import { parseAddressParts } from "../../../lib/address";

const COLUMNS = [
	{ label: "Name", key: "name", sortable: true },
	{ label: "Type", key: "type", sortable: true },
	{ label: "Address", key: "address", sortable: true },
	{ label: "City", key: "city", sortable: true },
	{ label: "State", key: "state", sortable: true },
	{ label: "Zip", key: "zip", sortable: true },
	{ label: "Phone", key: "phone", sortable: false },
	{ label: "Website", key: "website", sortable: false }
];

// Prefer the canonical `bar_address` (Google-enriched) parsed via the
// shared parser. Only fall back to individual OSM `addr:*` tags when a
// field is still empty after parsing. The old impl mixed tags with a
// homegrown split that produced frankenstein rows (e.g. street="1218"
// with empty city/state/zip when the only OSM tag was addr:housenumber,
// or empty street/zip when OSM had addr:city/state but no street/postcode).
const getAddressParts = (establishment) => {
	const tags = establishment.osm_metadata?.all_tags || {};
	const parsed = establishment.bar_address
		? parseAddressParts(establishment.bar_address)
		: {};

	let street = parsed.street || "";
	let city = parsed.city || "";
	let state = parsed.state || "";
	// Note: lib/address.js returns `postalCode`, not `zip`.
	let zip = parsed.postalCode || "";

	if (!street) {
		street = [tags["addr:housenumber"], tags["addr:street"]]
			.filter(Boolean)
			.join(" ");
	}
	if (!city) city = tags["addr:city"] || "";
	if (!state) state = tags["addr:state"] || "";
	if (!zip) zip = tags["addr:postcode"] || "";

	return { street, city, state, zip };
};

const getFieldValue = (establishment, key) => {
	switch (key) {
		case "name": return (establishment.bar_name || "").toLowerCase();
		case "type": return (establishment.osm_metadata?.amenity || "").toLowerCase();
		case "address": return getAddressParts(establishment).street.toLowerCase();
		case "city": return getAddressParts(establishment).city.toLowerCase();
		case "state": return getAddressParts(establishment).state.toLowerCase();
		case "zip": return getAddressParts(establishment).zip.toLowerCase();
		case "phone": return (establishment.bar_phone || "").toLowerCase();
		case "website": return (establishment.bar_url || "").toLowerCase();
		default: return "";
	}
};

const CollectedDataCard = ({
	osmRawData,
	selectedRows,
	onSelectAll,
	onSelectRow,
	onRequestDelete,
	onRefresh,
	loadingRawData,
	isMobile
}) => {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [sortField, setSortField] = useState(null);
	const [sortDirection, setSortDirection] = useState("asc");

	const handleColumnSort = useCallback((key) => {
		setSortField((prev) => {
			if (prev === key) {
				setSortDirection((d) => d === "asc" ? "desc" : "asc");
				return prev;
			}
			setSortDirection("asc");
			return key;
		});
		setPage(0);
	}, []);

	const sortedData = useMemo(() => {
		if (!sortField) return osmRawData;
		return [...osmRawData].sort((a, b) => {
			const aVal = getFieldValue(a, sortField);
			const bVal = getFieldValue(b, sortField);
			const cmp = aVal.localeCompare(bVal);
			return sortDirection === "asc" ? cmp : -cmp;
		});
	}, [osmRawData, sortField, sortDirection]);
	const latestCollectionDate = useMemo(() => {
		if (!osmRawData || osmRawData.length === 0) {
			return "—";
		}

		const latestTimestamp = Math.max(
			...osmRawData.map((item) => {
				if (!item.firebase_created_at) return 0;
				if (item.firebase_created_at.seconds) {
					return item.firebase_created_at.seconds * 1000;
				}
				if (item.firebase_created_at instanceof Date) {
					return item.firebase_created_at.getTime();
				}
				if (typeof item.firebase_created_at === "number") {
					return item.firebase_created_at;
				}
				return 0;
			})
		);

		return latestTimestamp > 0 ? new Date(latestTimestamp).toLocaleString() : "—";
	}, [osmRawData]);

	const amenityTypes = useMemo(() => {
		if (!osmRawData || osmRawData.length === 0) {
			return {};
		}

		return osmRawData.reduce((acc, item) => {
			const amenity = item.osm_metadata?.amenity || "unknown";
			acc[amenity] = (acc[amenity] || 0) + 1;
			return acc;
		}, {});
	}, [osmRawData]);

	const renderSummary = () => (
		<Box>
			<Typography variant="body2" color="textSecondary" gutterBottom>
				<strong>Types:</strong>
			</Typography>
			<Box display="flex" flexWrap="wrap" gap={1}>
				{Object.entries(amenityTypes).map(([type, count]) => (
					<Chip key={type} label={`${type}: ${count}`} size="small" variant="outlined" color="primary" />
				))}
			</Box>
		</Box>
	);

	return (
		<StyledCard>
			<CardContent>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
					<Typography variant="h6">Collected OSM Data ({osmRawData.length} records)</Typography>
					<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
						{selectedRows.size > 0 && (
							<>
								{!isMobile && (
									<Typography variant="body2" color="textSecondary">
										Excluded venues will not be added as bars.
									</Typography>
								)}
								<Button
									variant="outlined"
									color="primary"
									size="small"
									startIcon={<RemoveCircleOutline />}
									onClick={onRequestDelete}
								>
									Exclude {selectedRows.size} Row{selectedRows.size !== 1 ? "s" : ""}
								</Button>
							</>
						)}
						<Button variant="outlined" size="small" onClick={onRefresh} disabled={loadingRawData}>
							Refresh
						</Button>
					</Box>
				</Box>

				{loadingRawData ? (
					<Box display="flex" alignItems="center" gap={2}>
						<CircularProgress size={20} />
						<Typography>Loading existing OSM data...</Typography>
					</Box>
				) : osmRawData.length > 0 ? (
					<>
						<Box mb={2}>
							<Typography variant="body2" color="textSecondary">
								These records came from Open Street Maps. You may select any number of rows to exclude them from being added as bars.
							</Typography>
							<Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
								<strong>Latest collection:</strong> {latestCollectionDate}
							</Typography>
						</Box>

						<Box mb={2}>{renderSummary()}</Box>

						{isMobile ? (
							<>
								<Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.5} flexWrap="wrap">
									<Box display="flex" alignItems="center" gap={1}>
										<Checkbox
											size="small"
											checked={selectedRows.size === osmRawData.length && osmRawData.length > 0}
											indeterminate={selectedRows.size > 0 && selectedRows.size < osmRawData.length}
											onChange={onSelectAll}
										/>
										<Typography variant="body2" color="textSecondary">
											Select all
										</Typography>
									</Box>
									<FormControl size="small" sx={{ minWidth: 140 }}>
										<InputLabel>Sort by</InputLabel>
										<Select
											value={sortField ? `${sortField}-${sortDirection}` : ""}
											label="Sort by"
											onChange={(e) => {
												const val = e.target.value;
												if (!val) {
													setSortField(null);
													setSortDirection("asc");
												} else {
													const [field, dir] = val.split("-");
													setSortField(field);
													setSortDirection(dir);
												}
												setPage(0);
											}}
										>
											<MenuItem value="">None</MenuItem>
											{COLUMNS.filter((col) => col.sortable).map((col) => [
												<MenuItem key={`${col.key}-asc`} value={`${col.key}-asc`}>{col.label} (A→Z)</MenuItem>,
												<MenuItem key={`${col.key}-desc`} value={`${col.key}-desc`}>{col.label} (Z→A)</MenuItem>
											])}
										</Select>
									</FormControl>
								</Box>
								<Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: 600, overflowY: "auto" }}>
									{sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((establishment) => {
										const { street, city, state, zip } = getAddressParts(establishment);
										return (
										<MobileCard
											key={establishment.id}
											style={{
												borderColor: selectedRows.has(establishment.id) ? "#1976d2" : undefined,
												opacity: selectedRows.has(establishment.id) ? 0.7 : 1
											}}
										>
											<div className="card-header">
												<Box display="flex" alignItems="flex-start" gap={1} flex={1}>
													<Checkbox
														size="small"
														checked={selectedRows.has(establishment.id)}
														onChange={() => onSelectRow(establishment.id)}
														sx={{ mt: -0.5, ml: -0.5 }}
													/>
													<div>
														<strong>{establishment.bar_name || "Unnamed"}</strong>
													</div>
												</Box>
												<Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
													{establishment.osm_metadata?.amenity || "n/a"}
												</Typography>
											</div>
											<div className="card-row">
												<span className="card-label">Address</span>
												<span className="card-value">{street || "—"}</span>
											</div>
											<div className="card-row">
												<span className="card-label">City</span>
												<span className="card-value">{city || "—"}</span>
											</div>
											<div className="card-row">
												<span className="card-label">State / Zip</span>
												<span className="card-value">{[state, zip].filter(Boolean).join(" ") || "—"}</span>
											</div>
											<div className="card-row">
												<span className="card-label">Phone</span>
												<span className="card-value">{establishment.bar_phone || "—"}</span>
											</div>
											<div className="card-row">
												<span className="card-label">Website</span>
												<span className="card-value">
													{establishment.bar_url ? (
														<a
															href={establishment.bar_url}
															target="_blank"
															rel="noopener noreferrer"
															style={{ fontSize: "0.8rem", wordBreak: "break-all" }}
														>
															{establishment.bar_url.substring(0, 30)}{establishment.bar_url.length > 30 ? "..." : ""}
														</a>
													) : "—"}
												</span>
											</div>
										</MobileCard>
									);
									})}
								</Box>
								<TablePagination
									component="div"
									count={sortedData.length}
									page={page}
									onPageChange={(e, newPage) => setPage(newPage)}
									rowsPerPage={rowsPerPage}
									onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
									rowsPerPageOptions={[25, 50, 100]}
									sx={{ "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-displayedRows": { marginLeft: "auto" } }}
								/>
							</>
						) : (
						<Box
							sx={{
								overflowX: "auto",
								border: "1px solid #e0e0e0",
								borderRadius: "8px",
								position: "relative"
							}}
						>
							<Box
								sx={{
									maxHeight: "600px",
									overflowY: "auto"
								}}
							>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns:
											"40px 1.5fr 80px 1.2fr 100px 50px 60px 100px 1fr",
										gap: "1px",
										backgroundColor: "#f5f5f5"
									}}
								>
									<Box
										sx={{
											py: 0.5,
											px: 1,
											backgroundColor: "white",
											fontWeight: "bold",
											borderBottom: "2px solid #e0e0e0",
											position: "sticky",
											top: 0,
											zIndex: 10,
											display: "flex",
											alignItems: "center",
											justifyContent: "center"
										}}
									>
										<Checkbox
											size="small"
											checked={selectedRows.size === osmRawData.length && osmRawData.length > 0}
											indeterminate={selectedRows.size > 0 && selectedRows.size < osmRawData.length}
											onChange={onSelectAll}
										/>
									</Box>
									{COLUMNS.map((col) => {
										const active = sortField === col.key;
										return (
											<Box
												key={col.key}
												onClick={col.sortable ? () => handleColumnSort(col.key) : undefined}
												sx={{
													py: 0.5,
													px: 1,
													backgroundColor: "white",
													fontWeight: "bold",
													borderBottom: "2px solid #e0e0e0",
													position: "sticky",
													top: 0,
													zIndex: 10,
													fontSize: "0.85rem",
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													...(col.sortable ? {
														cursor: "pointer",
														userSelect: "none",
														"&:hover": { color: "primary.main" }
													} : {})
												}}
											>
												{col.label}
												{col.sortable && (
													<ExpandMore
														sx={{
															fontSize: 18,
															transition: "transform 0.2s, opacity 0.2s",
															transform: active && sortDirection === "asc" ? "rotate(180deg)" : "rotate(0deg)",
															opacity: active ? 1 : 0.35,
															color: active ? "primary.main" : "text.secondary"
														}}
													/>
												)}
											</Box>
										);
									})}

									{sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((establishment) => {
										const { street, city, state, zip } = getAddressParts(establishment);

										const cellSx = { py: 0.5, px: 1, backgroundColor: "white", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center" };
										return (
										<React.Fragment key={establishment.id}>
											<Box
												sx={{
													...cellSx,
													display: "flex",
													alignItems: "center",
													justifyContent: "center"
												}}
											>
												<Checkbox
													size="small"
													checked={selectedRows.has(establishment.id)}
													onChange={() => onSelectRow(establishment.id)}
												/>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: "medium" }}>
													{establishment.bar_name || "Unnamed"}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
													{establishment.osm_metadata?.amenity || "n/a"}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
													{street}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
													{city}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
													{state}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
													{zip}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
													{establishment.bar_phone || "—"}
												</Typography>
											</Box>
											<Box sx={cellSx}>
												{establishment.bar_url ? (
													<Typography
														variant="body2"
														sx={{
															fontSize: "0.75rem",
															color: "primary.main",
															cursor: "pointer",
															wordBreak: "break-all"
														}}
														component="a"
														href={establishment.bar_url}
														target="_blank"
														rel="noopener noreferrer"
													>
														{establishment.bar_url}
													</Typography>
												) : (
													<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>—</Typography>
												)}
											</Box>
										</React.Fragment>
										);
									})}
								</Box>
							</Box>
							<TablePagination
								component="div"
								count={sortedData.length}
								page={page}
								onPageChange={(e, newPage) => setPage(newPage)}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
								rowsPerPageOptions={[25, 50, 100]}
								sx={{ "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-displayedRows": { marginLeft: "auto" } }}
							/>
						</Box>
						)}
					</>
				) : (
					<Box textAlign="center" py={4}>
						<Typography variant="body1" color="textSecondary" gutterBottom>
							No OSM data collected yet
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Run the data collection above to populate this section with establishments from OpenStreetMap. Once data is collected, you&apos;ll be able to review and merge it with existing bar records.
						</Typography>
					</Box>
				)}
			</CardContent>
		</StyledCard>
	);
};

export default CollectedDataCard;
